// src/features/admin-options/components/AdminOptionsPage.tsx

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import {
  DevicesRounded as DeviceIcon,
  HotelRounded as HotelIcon,
  BusinessRounded as GroupIcon,
  PeopleRounded as UsersIcon,
  NotificationsActive as NotificationsIcon,
  SettingsRounded as SystemIcon,
  LocalOfferRounded as LabelIcon,
  BackupRounded as BackupIcon,
  TuneRounded as PreferencesIcon,
  HomeWorkRounded as HotelSettingsIcon,
  SettingsApplicationsRounded as RoommatikSettingsIcon,
} from '@mui/icons-material';
import { useTranslate, ResourceContext, useDataProvider } from 'react-admin';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { hasResourcePermission } from '@/config/resourceConfig';
import { getCurrentUser } from '@/utils/auth';
import { useCsvExport } from '@/hooks/useCsvExport';
import type { SubMenuItem } from '@/components/common/SubMenu';
import { AdminSectionNav } from './AdminSectionNav';
import { SectionHeader } from '@/components/common/SectionHeader';
import { FilterPopoverProvider, useFilterPopover } from '@/components/common/RaFilterPopover';
import {
  devicesHelpConfig,
  hotelsHelpConfig,
  groupsHelpConfig,
  usersHelpConfig,
} from '@/config/helpConfig';
import type { PageHelpConfig } from '@/components/common/PageHelp';

/** Clears the shared search box when the admin tab changes (each resource has its own search). */
const SearchResetOnTab: React.FC<{ view: string }> = ({ view }) => {
  const ctx = useFilterPopover();
  useEffect(() => {
    ctx?.setSearchValue('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);
  return null;
};

// Import the existing list components
import { DeviceList } from '@/features/devices';
import { HotelList } from '@/features/hotels';
import { GroupList } from '@/features/groups';
import { UserList } from '@/features/users';
import SystemInfoView from './SystemInfoView';
import { EventNotificationConfigHub } from './EventNotificationConfigHub';
import { LabelCatalogManager } from '@/features/labels/LabelCatalogManager';
import BackupsView from './BackupsView';
import { PreferencesTab } from './PreferencesTab';
import { HotelSettingsTab } from './HotelSettingsTab';
import { RoommatikSettingsTab } from './RoommatikSettingsTab';

type AdminView = 'devices' | 'hotels' | 'groups' | 'users' | 'notifications' | 'labels' | 'backups' | 'system' | 'preferences' | 'hotelSettings' | 'roommatikSettings';

const helpConfigs: Record<string, PageHelpConfig> = {
  devices: devicesHelpConfig,
  hotels: hotelsHelpConfig,
  groups: groupsHelpConfig,
  users: usersHelpConfig,
  hotelSettings: {
    titleKey: 'options.hotel.help.title',
    descriptionKey: 'options.hotel.help.description',
    featureKeys: ['options.hotel.help.features.thresholds', 'options.hotel.help.features.applyToGroup'],
  },
  roommatikSettings: {
    titleKey: 'options.roommatik.help.title',
    descriptionKey: 'options.roommatik.help.description',
    featureKeys: ['options.roommatik.help.features.retention'],
  },
};

/** Per-resource placeholder for the inline search field. */
const searchPlaceholderKeys: Record<string, string> = {
  devices: 'resources.devices.filters.search',
  hotels: 'resources.hotels.list.search',
  groups: 'resources.groups.list.search',
};

/**
 * Admin Options Page - Consolidated admin management
 *
 * Standard SectionHeader card: [SubMenu tabs] [inline search] … [Help] [Filters] [Export] [Create]
 * Below: child List (embedded) with its filters in the shared popover + datagrid.
 *
 * Access by role:
 * - SuperAdmin: Devices, Hotels, Groups, System
 * - GroupAdmin: Devices (read-only), Hotels (read-only)
 * - HotelAdmin: Devices (read-only)
 */
export const AdminOptionsPage: React.FC = () => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const [searchParams] = useSearchParams();

  const currentUser = getCurrentUser();
  const isSuperAdmin = currentUser?.role === 'SuperAdmin' || currentUser?.role === 'Admin';
  const isGroupAdmin = currentUser?.role === 'GroupAdmin';
  const assignedGroups = currentUser?.assignedGroups ?? [];
  const isMultiGroupAdmin = isGroupAdmin && assignedGroups.length > 1;
  const showHotelGroupFilter = isSuperAdmin || isMultiGroupAdmin;

  // Build available tabs based on permissions
  const availableTabs = useMemo(() => {
    const tabs: SubMenuItem[] = [];

    // «Mis preferencias» (mockup 13, first tab) — every role sees it, first in the list.
    tabs.push({
      value: 'preferences',
      label: translate('preferences.tabs.mine'),
      icon: <PreferencesIcon />,
    });

    // «Opciones del hotel» (mockup 13, second tab) — HotelAdmin+ for their own hotels.
    const roleForSettings = getCurrentUser()?.role;
    if (roleForSettings && ['HotelAdmin', 'GroupAdmin', 'Admin', 'SuperAdmin'].includes(roleForSettings)) {
      tabs.push({
        value: 'hotelSettings',
        label: translate('options.hotel.tabLabel'),
        icon: <HotelSettingsIcon />,
      });
    }

    // «Opciones de Roommatik» (mockup 13, third tab) — Admin/SuperAdmin only.
    if (roleForSettings === 'Admin' || roleForSettings === 'SuperAdmin') {
      tabs.push({
        value: 'roommatikSettings',
        label: translate('options.roommatik.tabLabel'),
        icon: <RoommatikSettingsIcon />,
      });
    }

    if (hasResourcePermission('devices', 'list')) {
      tabs.push({
        value: 'devices',
        label: translate('resources.adminOptions.tabs.devices'),
        icon: <DeviceIcon />,
      });
    }
    if (hasResourcePermission('hotels', 'list')) {
      tabs.push({
        value: 'hotels',
        label: translate('resources.adminOptions.tabs.hotels'),
        icon: <HotelIcon />,
      });
    }
    if (hasResourcePermission('groups', 'list')) {
      tabs.push({
        value: 'groups',
        label: translate('resources.adminOptions.tabs.groups'),
        icon: <GroupIcon />,
      });
    }
    if (hasResourcePermission('users', 'list')) {
      tabs.push({
        value: 'users',
        label: translate('resources.adminOptions.tabs.users'),
        icon: <UsersIcon />,
      });
    }

    // Notifications tab — SuperAdmin + Admin + GroupAdmin
    const user = getCurrentUser();
    if (user?.role === 'SuperAdmin' || user?.role === 'Admin' || user?.role === 'GroupAdmin') {
      tabs.push({
        value: 'notifications',
        label: translate('resources.adminOptions.tabs.notifications'),
        icon: <NotificationsIcon />,
      });
    }

    // Labels catalog tab — SuperAdmin + Admin + GroupAdmin (curate their own catalog)
    if (user?.role === 'SuperAdmin' || user?.role === 'Admin' || user?.role === 'GroupAdmin') {
      tabs.push({
        value: 'labels',
        label: translate('resources.adminOptions.tabs.labels'),
        icon: <LabelIcon />,
      });
    }

    // Backups tab — strictly SuperAdmin (Admin explicitly excluded)
    if (user?.role === 'SuperAdmin') {
      tabs.push({
        value: 'backups',
        label: translate('resources.adminOptions.tabs.backups'),
        icon: <BackupIcon />,
      });
    }

    // System tab — SuperAdmin + Admin
    if (user?.role === 'SuperAdmin' || user?.role === 'Admin') {
      tabs.push({
        value: 'system',
        label: translate('resources.adminOptions.tabs.system'),
        icon: <SystemIcon />,
      });
    }

    return tabs;
  }, [translate]);

  const tabParam = searchParams.get('tab') as AdminView | null;
  const defaultView = (tabParam && availableTabs.some(t => t.value === tabParam))
    ? tabParam
    : (availableTabs.length > 0 ? availableTabs[0].value as AdminView : 'devices');
  const [activeView, setActiveView] = useState<AdminView>(defaultView);

  const isListView = activeView !== 'system' && activeView !== 'notifications' && activeView !== 'labels' && activeView !== 'backups' && activeView !== 'preferences' && activeView !== 'hotelSettings' && activeView !== 'roommatikSettings';
  const canCreate = isListView && hasResourcePermission(activeView, 'create');
  const canExport = isListView && hasResourcePermission(activeView, 'export');

  const handleCreate = () => navigate(`/${activeView}/create`);

  // Decision 9 CSV format (`;` separator, UTF-8 BOM, es headers, filename
  // insight-<page>-<from>-<to>.csv) via the shared hook point; columns are
  // built dynamically from whatever fields the list actually returned since
  // this page covers four different resources with one export button.
  const exportCsv = useCsvExport({ page: activeView });

  const handleExport = useCallback(async () => {
    try {
      // Users export mirrors the standalone Users screen: roles above the
      // current user's rank are excluded client-side as a security layer.
      const exportFilter =
        activeView === 'users' && currentUser?.role && currentUser.role !== 'SuperAdmin'
          ? { excludeRoles: currentUser.role === 'Admin' ? ['SuperAdmin'] : ['SuperAdmin', 'Admin'] }
          : {};
      const { data } = await dataProvider.getList(activeView, {
        filter: exportFilter,
        sort: { field: 'id', order: 'ASC' },
        pagination: { page: 1, perPage: 10000 },
      });
      if (!data?.length) return;
      const rows = data as unknown as Record<string, unknown>[];
      const columns = Object.keys(rows[0]).map((key) => ({
        labelKey: `resources.${activeView}.fields.${key}`,
        value: (row: Record<string, unknown>) => {
          const cell = row[key];
          return cell === null || cell === undefined || typeof cell === 'object' ? '' : (cell as string | number);
        },
      }));
      await exportCsv(rows, columns);
    } catch (e) {
      console.error('Export failed:', e);
    }
  }, [activeView, dataProvider, currentUser?.role, exportCsv]);

  // Help config for current view (none for system)
  const helpConfig = helpConfigs[activeView];
  // Inline search only when search is the sole filter (groups always; hotels when
  // the group filter is hidden). Otherwise search lives in the popup.
  const isSearchOnly = isListView && (
    activeView === 'groups' || (activeView === 'hotels' && !showHotelGroupFilter)
  );
  const searchPlaceholder = isSearchOnly
    ? translate(searchPlaceholderKeys[activeView] ?? 'ra.action.search')
    : undefined;

  // List sections carry their title in the header row; the other sections render their own heading.
  const activeLabel = availableTabs.find((t) => t.value === activeView)?.label;
  const headerLeft = isListView && activeLabel ? (
    <Typography variant="h2" component="h2" noWrap sx={{ fontSize: '1rem', fontWeight: 600, color: 'text.primary' }}>
      {activeLabel}
    </Typography>
  ) : undefined;

  return (
    <FilterPopoverProvider>
      <SearchResetOnTab view={activeView} />
      <Box sx={{ p: { xs: 1, sm: 1 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
        <AdminSectionNav
          items={availableTabs}
          activeItem={activeView}
          onChange={(value) => setActiveView(value as AdminView)}
        />
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
        {/* Slim header row: section title + actions (help, manual, search, filters, export, create) */}
        <SectionHeader manualScreen="admin-options"
          title={null}
          helpConfig={helpConfig}
          left={headerLeft}
          useRaFilters={isListView}
          showSearch={isSearchOnly}
          searchPlaceholder={searchPlaceholder}
          showExport={canExport}
          onExportClick={handleExport}
          showCreate={canCreate}
          onCreateClick={handleCreate}
        />

        {/* Content below the header */}
        <Box>
          {activeView === 'preferences' && (
            <PreferencesTab />
          )}
          {activeView === 'hotelSettings' && (
            <HotelSettingsTab />
          )}
          {activeView === 'roommatikSettings' && (
            <RoommatikSettingsTab />
          )}
          {activeView === 'devices' && hasResourcePermission('devices', 'list') && (
            <ResourceContext.Provider value="devices">
              <DeviceList embedded />
            </ResourceContext.Provider>
          )}
          {activeView === 'hotels' && hasResourcePermission('hotels', 'list') && (
            <ResourceContext.Provider value="hotels">
              <HotelList embedded />
            </ResourceContext.Provider>
          )}
          {activeView === 'groups' && hasResourcePermission('groups', 'list') && (
            <ResourceContext.Provider value="groups">
              <GroupList embedded />
            </ResourceContext.Provider>
          )}
          {activeView === 'users' && hasResourcePermission('users', 'list') && (
            <ResourceContext.Provider value="users">
              <UserList embedded />
            </ResourceContext.Provider>
          )}
          {activeView === 'notifications' && (
            <EventNotificationConfigHub />
          )}
          {activeView === 'labels' && (
            <LabelCatalogManager />
          )}
          {activeView === 'backups' && getCurrentUser()?.role === 'SuperAdmin' && (
            <BackupsView />
          )}
          {activeView === 'system' && (
            <SystemInfoView />
          )}
        </Box>
        </Box>
        </Box>
      </Box>
    </FilterPopoverProvider>
  );
};

export default AdminOptionsPage;
